import React from 'react';
import { ReactRelayContext } from 'react-relay';
import { useInvokeExtension } from '../web-client';
import { useTracingContext } from '../error-reporting';
import { ThreeLOPrompt } from '../components/threeLOPrompt';
import type { IframeProps } from '../custom-ui/iframe/types';
import type { BridgeServices, ModalController } from './types';
import { EnvironmentContext } from '../context';
import { useProductFetchClient } from '../custom-ui/bridge/methods/useProductFetchClient';

type UseBridgeServicesInput = Pick<
	IframeProps,
	'contextIds' | 'extension' | 'apolloClient' | 'getContextToken' | 'coreData'
> & {
	appId: string;
};

/**
 * Assembles host-side capabilities required by bridge methods and returns a
 * stable `getServices` getter that always reflects the latest values.
 *
 * Rather than returning a frozen `BridgeServices` snapshot, the hook updates
 * a `servicesRef` synchronously on every render and exposes a stable getter
 * (created once via `useRef`) that reads from that ref at call time. This
 * ensures all services — `invoker`, `getContextToken`, `productFetchClient`,
 * etc. — are always current when a bridge method is invoked, regardless of
 * which upstream dep changed, without per-service ref boilerplate.
 *
 * Throws at mount time if required capabilities are missing, so bridge methods
 * can use all services without defensive optional-chaining.
 */
export const useBridgeServices = (
	props: UseBridgeServicesInput,
	modalController: ModalController,
): { getServices: () => BridgeServices } => {
	const invoker = useInvokeExtension(props.contextIds, props.extension.id, {
		client: props.apolloClient,
	});
	const tracing = useTracingContext();
	const environment = React.useContext(EnvironmentContext);
	const relayContext = React.useContext(ReactRelayContext);

	const isAnonymousAccessSupportedByApp =
		props.extension.properties?.unlicensedAccess?.includes('anonymous') ?? false;

	const onThreeLO = makeOnThreeLO(modalController);

	// Adapts the two-argument onThreeLO (authUrl, retry) into the single-argument
	// shape expected by useProductFetchClient. Falls back to a no-op when the
	// extension has no consentUrl (e.g. sandbox/test environments).
	const renderThreeLOPrompt = (retry: () => Promise<void>): Promise<void> => {
		const consentUrl = props.extension.consentUrl;
		if (!consentUrl) {
			return Promise.resolve();
		}
		return onThreeLO(consentUrl, retry);
	};

	const productFetchClient = useProductFetchClient({
		appId: props.appId,
		environment,
		contextIds: props.contextIds,
		cloudId: props.coreData.cloudId,
		workspaceId: props.coreData.workspaceId,
		extensionId: props.extension.id,
		isAnonymousAccessSupportedByApp,
		renderThreeLOPrompt,
		options: { client: props.apolloClient! },
	});

	// All hook calls must precede the tracing guard below.
	// servicesRef holds the latest assembled BridgeServices snapshot and is updated
	// synchronously every render. The stable getter reads from it at bridge-call time,
	// so every service (invoker, getContextToken, productFetchClient, etc.) is always
	// current — regardless of which dep changed — without per-service ref boilerplate.
	const servicesRef = React.useRef<BridgeServices | null>(null);
	const getServices = React.useRef<() => BridgeServices>(() => servicesRef.current!).current;

	if (!tracing) {
		throw new Error('WorkerWebRuntime: TracingProvider not found in the component tree. ');
	}

	if (!relayContext?.environment) {
		throw new Error('useBridgeServices: RelayEnvironmentProvider not found in the component tree.');
	}

	// Assembled fresh every render; updates the ref synchronously so any in-flight
	// bridge call that fires after this render always sees the latest values.
	servicesRef.current = {
		invoker,
		getContextToken: props.getContextToken ?? nullGetContextToken,
		tracing,
		modalController,
		onThreeLO,
		productFetchClient,
		relayEnvironment: relayContext.environment,
	};

	return { getServices };
};

// TODO: currently sandbox does not support getContextToken (unlike other products),
// so we provide a noop implementation to avoid having to guard every bridge method that uses it.
const nullGetContextToken = (() => undefined) as unknown as BridgeServices['getContextToken'];

/**
 * Factory that produces the `onThreeLO` service callback for BridgeServices.
 *
 * Separating this from `useRuntimeModalController` keeps `ModalController`
 * generic and free from domain-specific knowledge.  The returned function shows
 * the 3LO consent prompt via `modalController.show()` and resolves the returned
 * Promise after the user grants consent and `retry()` completes.
 *
 * The returned callback must be kept stable by the caller (e.g. stored in a ref)
 * because it is placed in BridgeServices and captured by bridge method closures.
 */
export const makeOnThreeLO =
	(modalController: ModalController): BridgeServices['onThreeLO'] =>
	(authUrl: string, retry: () => Promise<void>): Promise<void> =>
		new Promise((resolve, reject) => {
			modalController.show(
				React.createElement(ThreeLOPrompt, {
					authUrl,
					onSuccess: async () => {
						modalController.hide();
						try {
							await retry();
							resolve();
						} catch (e) {
							reject(e);
						}
					},
				}),
			);
		});
